import paper from "paper";
import { normalizeRotation } from "./constructionRotation";
import type { ConstructionLine, Project } from "./Project";

/**
 * Mirror a construction line in place about a pivot (issue #346 — the
 * Flip buttons). "horizontal" reflects across the vertical line `x = cx`
 * (left ⇄ right); "vertical" reflects across `y = cy` (top ⇄ bottom).
 *
 * Mirrors the structure of `rotateConstructionLine`: it transforms the
 * pathData and every parallel geometry field (primitive, corner-rounding
 * source, freehand raw points) so the derive passes don't clobber the
 * flipped shape, and negates any tracked rotation so the bbox-tilt
 * overlay stays correct for already-rotated lines.
 */
export type FlipAxis = "horizontal" | "vertical";

type Primitive = NonNullable<Project["constructionLines"]>[number]["primitive"];

/** Reflect SVG pathData about the pivot via Paper.js. Returns the input
 *  unchanged on empty / unparseable data. */
export function flipPathData(
  d: string,
  axis: FlipAxis,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): string {
  if (!d) return d;
  try {
    const cp = new scope.CompoundPath({ pathData: d, insert: false });
    const center = new scope.Point(cx, cy);
    if (axis === "horizontal") cp.scale(-1, 1, center);
    else cp.scale(1, -1, center);
    return cp.pathData;
  } catch {
    return d;
  }
}

/** Reflect a primitive's parameters. Rectangle / ellipse keep their
 *  axis-aligned bbox encoding (only the origin corner moves); endpoint
 *  primitives (line / pill) reflect each endpoint. */
export function flipPrimitive(
  prim: Primitive,
  axis: FlipAxis,
  cx: number,
  cy: number,
): Primitive {
  if (!prim) return prim;
  if (prim.kind === "rectangle" || prim.kind === "ellipse") {
    return axis === "horizontal"
      ? { ...prim, x: 2 * cx - (prim.x + prim.width) }
      : { ...prim, y: 2 * cy - (prim.y + prim.height) };
  }
  const fx = (x: number) => (axis === "horizontal" ? 2 * cx - x : x);
  const fy = (y: number) => (axis === "vertical" ? 2 * cy - y : y);
  return {
    ...prim,
    x1: fx(prim.x1),
    y1: fy(prim.y1),
    x2: fx(prim.x2),
    y2: fy(prim.y2),
  };
}

/** Reflect a list of points about the pivot. */
export function flipPoints(
  pts: ReadonlyArray<{ x: number; y: number }>,
  axis: FlipAxis,
  cx: number,
  cy: number,
): { x: number; y: number }[] {
  return pts.map((p) => ({
    x: axis === "horizontal" ? 2 * cx - p.x : p.x,
    y: axis === "vertical" ? 2 * cy - p.y : p.y,
  }));
}

export function flipConstructionLine(
  line: ConstructionLine,
  axis: FlipAxis,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): ConstructionLine {
  const next: ConstructionLine = {
    ...line,
    pathData: flipPathData(line.pathData, axis, cx, cy, scope),
  };
  next.primitive = flipPrimitive(line.primitive, axis, cx, cy);
  // Reversible-primitive snapshot rides along, same as rotation does.
  if (line.convertedFromPrimitive) {
    next.convertedFromPrimitive = {
      ...line.convertedFromPrimitive,
      primitive: flipPrimitive(
        line.convertedFromPrimitive.primitive,
        axis,
        cx,
        cy,
      )!,
    };
  }
  // A reflection reverses the sense of rotation: a line tracked at +r°
  // about a pivot reads as −r° about the reflected pivot afterwards.
  // Untracked (axis-aligned) lines have no rotation field to touch.
  if (line.rotation != null && Math.abs(line.rotation) > 1e-6) {
    next.rotation = normalizeRotation(-line.rotation);
    if (line.rotationCenter) {
      next.rotationCenter = {
        x: axis === "horizontal" ? 2 * cx - line.rotationCenter.x : line.rotationCenter.x,
        y: axis === "vertical" ? 2 * cy - line.rotationCenter.y : line.rotationCenter.y,
      };
    }
  }
  // The user-positioned pivot (issue #420) is a plain point that
  // reflects across the flip axis — independent of whether the line
  // tracks a rotation (a 0° line can still carry a custom pivot).
  if (line.pivotPoint) {
    next.pivotPoint = {
      x: axis === "horizontal" ? 2 * cx - line.pivotPoint.x : line.pivotPoint.x,
      y: axis === "vertical" ? 2 * cy - line.pivotPoint.y : line.pivotPoint.y,
    };
  }
  if (line.cornerRounding) {
    next.cornerRounding = {
      ...line.cornerRounding,
      sharpPathData: flipPathData(
        line.cornerRounding.sharpPathData,
        axis,
        cx,
        cy,
        scope,
      ),
    };
  }
  if (line.rawPoints) {
    next.rawPoints = flipPoints(line.rawPoints, axis, cx, cy);
  }
  return next;
}
